import React from 'react'
import { DataGrid } from '@mui/x-data-grid'
import { GroupedDataGridProps } from './types'
import { useGroupedData } from './hooks/useGroupedData'
import { useExpandedGroups } from './hooks/useExpandedGroups'
import { useSelection } from './hooks/useSelection'
import { useAggregation } from './hooks/useAggregation'
import { useColumnRenderer } from './components/ColumnRenderer'
import { useStyles } from './components/styles'

/**
 * Enhanced DataGrid with row grouping functionality
 */
const GroupedDataGrid: React.FC<GroupedDataGridProps> = ({
  data,
  groupByFields,
  columns: originalColumns,
  onSelectionChange,
  onSortModelChange,
  onFilterModelChange,
  pagination = true,
  pageSize = 10,
  checkboxSelection,
  checkboxSelectionMode = 'all',
  groupTitleFormatter,
  renderGroupTitle: externalRenderGroupTitle,
  parentInfoFields = [],
  autoExpandSingleChild = false,
  getRowId: customGetRowId,
  defaultExpandAll = false,
  ...rest
}) => {
  // Use the extracted hooks
  const { expandedGroups, handleToggleGroup } = useExpandedGroups({
    data,
    groupByFields,
    autoExpandSingleChild,
    defaultExpandAll
  })

  const processedRows = useGroupedData({
    data,
    groupByFields,
    expandedGroups,
    parentInfoFields,
    getRowId: customGetRowId
  })

  const { selection, handleSelectionChange } = useSelection({
    onSelectionChange,
    rows: processedRows
  })

  const { getGroupAggregation } = useAggregation()

  const columns = useColumnRenderer({
    originalColumns,
    groupByFields,
    parentInfoFields,
    expandedGroups,
    handleToggleGroup,
    getGroupAggregation,
    groupTitleFormatter,
    externalRenderGroupTitle
  })

  const { getRowClassName, getCheckboxVisibilityStyles, isRowSelectable } =
    useStyles({
      checkboxSelectionMode
    })

  // Assemble DataGrid properties
  const dataGridProps: any = {
    ...rest,
    rows: processedRows,
    columns,
    getRowClassName,
    onRowSelectionModelChange: handleSelectionChange,
    rowSelectionModel: selection,
    pagination,
    getRowId: (row: any) => {
      // Use custom getRowId if provided and row is not a group or child row
      if (customGetRowId && !row.isGroupRow && !row.isChildRow) {
        return customGetRowId(row)
      }
      return row.id
    },
    initialState: {
      pagination: {
        paginationModel: { pageSize, page: 0 }
      }
    },
    pageSizeOptions: [5, 10, 25, 50, 100],
    onSortModelChange,
    onFilterModelChange,
    sx: {
      '& .group-row': {
        // backgroundColor: 'rgba(0, 0, 0, 0.04)',
        // fontWeight: 'bold'
      },
      '& .child-row': {
        // borderLeft: '2px solid #1976d2'
        // backgroundColor: 'rgba(25, 118, 210, 0.04)'
      },
      '& .MuiDataGrid-cell': {
        // borderBottom: '1px solid rgba(224, 224, 224, 1)'
      },
      // Apply checkbox visibility styles
      ...getCheckboxVisibilityStyles(),
      ...(rest.sx || {})
    }
  }

  // Only add checkboxSelection property when it's true
  if (checkboxSelection === true) {
    dataGridProps.checkboxSelection = true
    dataGridProps.isRowSelectable = isRowSelectable
  }

  return <DataGrid {...dataGridProps} />
}

export default GroupedDataGrid
